/**
 * Wire normalizer for the node-ID tag data plane (`POST /tags/read`).
 *
 * The proxy returns one result row per requested graph node ID, in
 * snake_case, with a structured `error` object on failure rows. Every
 * consumer (Plant Viewer live-tag panels, the dashboard sandbox runtime)
 * parses that same wire shape through this function and then adapts the
 * normalized row to its own domain type. Pure data — no fetch, no React.
 */

/** One normalized `POST /tags/read` result row. */
export interface TagReadResultRow {
  /** Graph URN the row was requested by. */
  nodeId: string
  /** Registry tag path when known; empty for unregistered rows. */
  tagPath: string
  value: unknown
  quality?: string
  isGood?: boolean
  tagType?: string
  /** Acquisition timestamp (RFC 3339) when supplied by the registry. */
  timestamp?: string
  /** Acquisition route that supplied the value (`webdev`, `opc`, …). */
  source?: string
  /** Whether the cached value exceeded its freshness budget. */
  stale?: boolean
  /** Stable machine-readable reason for a failed or pending read. */
  errorCode?: string
  /** Stable machine-readable advisory that does not invalidate the value. */
  warning?: string
  /** True when the registry has no row for this graph node ID. */
  unregistered: boolean
  error?: string
}

/** Normalize `POST /tags/read` result rows. Rows without a `node_id` are dropped. */
export function normalizeTagReadResults(raw: unknown): TagReadResultRow[] {
  if (!Array.isArray(raw)) return []
  const rows: TagReadResultRow[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue
    const record = item as Record<string, unknown>
    const nodeId = typeof record['node_id'] === 'string' ? record['node_id'] : ''
    if (!nodeId) continue

    const rawError = record['error']
    const errorRecord =
      rawError && typeof rawError === 'object' && !Array.isArray(rawError)
        ? (rawError as Record<string, unknown>)
        : null
    const error =
      typeof rawError === 'string'
        ? rawError
        : typeof errorRecord?.['message'] === 'string'
          ? errorRecord['message']
          : undefined
    const errorCode = typeof errorRecord?.['code'] === 'string' ? errorRecord['code'] : undefined

    const quality = typeof record['quality'] === 'string' ? record['quality'] : undefined
    const isGood = typeof record['is_good'] === 'boolean' ? record['is_good'] : undefined
    const tagType = typeof record['tag_type'] === 'string' ? record['tag_type'] : undefined
    const timestamp = typeof record['timestamp'] === 'string' ? record['timestamp'] : undefined
    const source = typeof record['source'] === 'string' ? record['source'] : undefined
    const stale = typeof record['stale'] === 'boolean' ? record['stale'] : undefined
    const warning = typeof record['warning'] === 'string' ? record['warning'] : undefined

    rows.push({
      nodeId,
      tagPath: typeof record['tag_path'] === 'string' ? record['tag_path'] : '',
      value: record['value'],
      unregistered: errorRecord?.['code'] === 'TAG_NODE_UNKNOWN',
      ...(quality ? { quality } : {}),
      ...(isGood !== undefined ? { isGood } : {}),
      ...(tagType ? { tagType } : {}),
      ...(timestamp ? { timestamp } : {}),
      ...(source ? { source } : {}),
      ...(stale !== undefined ? { stale } : {}),
      ...(errorCode ? { errorCode } : {}),
      ...(warning ? { warning } : {}),
      ...(error ? { error } : {})
    })
  }
  return rows
}

/**
 * Align normalized tag-read rows to the requested node-ID order.
 *
 * Unexpected rows are ignored, duplicate requested IDs fail closed, and the
 * caller supplies its domain-specific placeholder for omitted IDs.
 */
export function alignTagReadResultsToRequest<T extends { nodeId: string }>(
  requestedNodeIds: readonly string[],
  rows: readonly T[],
  createMissingRow: (nodeId: string) => T
): T[] {
  const requested = new Set(requestedNodeIds)
  const byNodeId = new Map<string, T>()
  for (const row of rows) {
    if (!requested.has(row.nodeId)) continue
    if (byNodeId.has(row.nodeId)) {
      throw new Error(`Tag read response contained duplicate node ID: ${row.nodeId}`)
    }
    byNodeId.set(row.nodeId, row)
  }
  return requestedNodeIds.map((nodeId) => byNodeId.get(nodeId) ?? createMissingRow(nodeId))
}
